import { api } from './api.js';
import type { ApiResponse, PaginatedResponse } from '@elmes/shared';

// Atlas types (inferred from backend schemas)
export interface AtlasNode {
  id: number;
  slug: string;
  type: string;
  title: string;
  contentMd: string | null;
  metadata: Record<string, unknown> | null;
  parentId: number | null;
  position: { x: number; y: number } | null;
  importance: number;
  ownerProfileId: number | null;
  aiGenerated: boolean;
  source: string;
  driftStatus: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AtlasEdge {
  id: number;
  sourceId: number;
  targetId: number;
  kind: string;
  weight: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface AtlasNodeWithEdges extends AtlasNode {
  incomingEdges?: AtlasEdge[];
  outgoingEdges?: AtlasEdge[];
}

export const atlasService = {
  // Nodes
  async listNodes(params?: Record<string, string | number>) {
    const res = await api.get<ApiResponse<{ nodes: AtlasNode[]; pagination: PaginatedResponse<AtlasNode>['pagination'] }>>('/atlas/nodes', { params });
    return {
      success: res.data.success,
      data: res.data.data?.nodes ?? [],
      pagination: res.data.data?.pagination,
    };
  },

  async getNodeById(id: number) {
    const res = await api.get<ApiResponse<AtlasNodeWithEdges>>(`/atlas/nodes/${id}`);
    return res.data.data!;
  },

  async getNodeBySlug(slug: string) {
    const res = await api.get<ApiResponse<AtlasNode>>(`/atlas/by-slug/${slug}`);
    return res.data.data!;
  },

  async createNode(data: Record<string, unknown>) {
    const res = await api.post<ApiResponse<AtlasNode>>('/atlas/nodes', data);
    return res.data.data!;
  },

  async updateNode(id: number, data: Record<string, unknown>) {
    const res = await api.put<ApiResponse<AtlasNode>>(`/atlas/nodes/${id}`, data);
    return res.data.data!;
  },

  async deleteNode(id: number) {
    const res = await api.delete<ApiResponse>(`/atlas/nodes/${id}`);
    return res.data;
  },

  // Edges
  async listEdges(nodeId: number) {
    const res = await api.get<ApiResponse<AtlasEdge[] | { incoming: AtlasEdge[]; outgoing: AtlasEdge[] }>>(`/atlas/nodes/${nodeId}/edges`);
    const payload = res.data.data;
    if (Array.isArray(payload)) {
      return {
        incoming: payload.filter((e) => e.targetId === nodeId),
        outgoing: payload.filter((e) => e.sourceId === nodeId),
      };
    }
    return payload ?? { incoming: [], outgoing: [] };
  },

  async listAllEdges() {
    const res = await api.get<ApiResponse<AtlasEdge[]>>('/atlas/edges');
    return res.data.data!;
  },

  async createEdge(data: { sourceId: number; targetId: number; kind: string; weight?: number; metadata?: Record<string, unknown> }) {
    const res = await api.post<ApiResponse<AtlasEdge>>('/atlas/edges', data);
    return res.data.data!;
  },

  async deleteEdge(id: number) {
    const res = await api.delete<ApiResponse>(`/atlas/edges/${id}`);
    return res.data;
  },

  // Traverse
  async traverse(nodeId: number, params?: { direction?: string; depth?: number }) {
    const res = await api.get<ApiResponse<{ nodes: AtlasNode[]; edges: AtlasEdge[] }>>(`/atlas/nodes/${nodeId}/traverse`, { params });
    return res.data.data!;
  },

  // Sources
  async listSources(nodeId: number) {
    const res = await api.get<ApiResponse<unknown[]>>(`/atlas/nodes/${nodeId}/sources`);
    return res.data.data!;
  },

  async addSource(nodeId: number, data: { sourceType: string; sourceRef: string; metadata?: Record<string, unknown> }) {
    const res = await api.post<ApiResponse<unknown>>(`/atlas/nodes/${nodeId}/sources`, data);
    return res.data.data!;
  },

  // Drift
  async markDrift(nodeId: number, data: { driftStatus: string; reason?: string }) {
    const res = await api.post<ApiResponse<AtlasNode>>(`/atlas/nodes/${nodeId}/drift`, data);
    return res.data.data!;
  },

  // Context Pack
  async exportContextPack(data: { scope?: string | string[]; depth?: number; tokenBudget?: number; nodeIds?: number[] }) {
    const res = await api.post<ApiResponse<{ pack: string; tokenCount: number; nodeCount: number }>>('/atlas/context-pack', data);
    return res.data.data!;
  },
};
